import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RuntimeConfig } from '@umijs/max';
import { history } from '@umijs/max';

// Mako's HMR handler treats ANY window error event as fatal and triggers a full reload.
// ResizeObserver loop notifications are benign browser bookkeeping — suppress them in
// capture phase so Mako never sees them.
if (process.env.NODE_ENV === 'development') {
  window.addEventListener(
    'error',
    (e) => {
      if (
        e.message ===
        'ResizeObserver loop completed with undelivered notifications.'
      ) {
        e.stopImmediatePropagation();
      }
    },
    true,
  );
}

import {
  ActingAsMerchantBanner,
  AvatarDropdown,
  AvatarName,
  Footer,
  MerchantInitializer,
  SelectLang,
} from '@/components';
import { getActingAsMerchantOwner } from '@/models/actingAsMerchant';
import { currentUser as queryCurrentUser } from '@/services/auth/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const merchantsPath = '/merchants';

// Pages that don't require authentication
const authWhitelist = [
  '/user/login',
  '/user/register',
  '/user/register-result',
  '/user/reset-password',
  '/accept-invitation',
];

// Pages accessible without merchants (for new users)
const noMerchantWhitelist = [
  '/merchants',
  '/account/settings',
  '/account/staff-management',
  '/onboarding/callback', // Keep callback for Adyen redirect
  '/admin', // SuperAdmin routes don't need merchants
];

// Helper to check if user has merchants using user-specific key
const checkUserHasMerchants = (userId?: string): boolean => {
  if (!userId) return false;
  const merchantsKey = `aimerpay_merchants_${userId}`;
  const merchantsJson = localStorage.getItem(merchantsKey);
  if (merchantsJson) {
    try {
      const merchants = JSON.parse(merchantsJson);
      return Array.isArray(merchants) && merchants.length > 0;
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    // Check if we have a token
    const token = localStorage.getItem('aimerpay_token');
    if (!token) {
      const currentPath = history.location.pathname;
      if (!authWhitelist.includes(currentPath)) {
        history.push(loginPath);
      }
      return undefined;
    }

    try {
      const result = await queryCurrentUser({
        skipErrorHandler: true,
      });
      if (result.success && result.data) {
        // Map user data for compatibility
        return {
          ...result.data,
          name: result.data.displayName || result.data.username,
          userid: result.data.id,
          access: result.data.role,
        };
      }
      // Token invalid, clear and redirect
      localStorage.removeItem('aimerpay_token');
      localStorage.removeItem('aimerpay_refresh_token');
      localStorage.removeItem('aimerpay_token_expires');
      const currentPath = history.location.pathname;
      if (!authWhitelist.includes(currentPath)) {
        history.push(loginPath);
      }
    } catch (_error) {
      // Clear tokens on error
      localStorage.removeItem('aimerpay_token');
      localStorage.removeItem('aimerpay_refresh_token');
      localStorage.removeItem('aimerpay_token_expires');
      // Only redirect to login if not already on auth pages
      const currentPath = history.location.pathname;
      if (!authWhitelist.includes(currentPath)) {
        history.push(loginPath);
      }
    }
    return undefined;
  };

  const { pathname } = history.location;

  // Skip fetching user info for auth pages (login, register, reset-password)
  // But keep fetchUserInfo available for use after successful login
  if (authWhitelist.includes(pathname)) {
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout API: https://procomponents.ant.design/components/layout
export const layout: RuntimeConfig['layout'] = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_: any, avatarChildren: React.ReactNode) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // Watermark disabled
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { pathname } = history.location;

      // Check authentication
      if (!initialState?.currentUser && !authWhitelist.includes(pathname)) {
        history.push(loginPath);
        return;
      }

      // SuperAdmin doesn't need merchants - skip merchant check
      const userRole =
        initialState?.currentUser?.role || initialState?.currentUser?.access;
      if (userRole === 'SuperAdmin') {
        // If SuperAdmin is in merchant mode but navigated back to admin pages
        // (e.g. via browser back button), auto-exit merchant mode
        if (pathname.startsWith('/admin') && getActingAsMerchantOwner()) {
          sessionStorage.removeItem('aimerpay_acting_as_merchant_owner');
          sessionStorage.removeItem('aimerpay_acting_as_merchants_cache');
          setInitialState((prev: any) => ({ ...prev }));
        }

        // SuperAdmin not in merchant mode — redirect away from merchant pages
        if (
          !getActingAsMerchantOwner() &&
          !pathname.startsWith('/admin') &&
          !pathname.startsWith('/account') &&
          !pathname.startsWith('/user')
        ) {
          history.push('/admin/merchant-owners');
        }
        return;
      }

      // Check if user has merchants (for new users without merchants)
      // Skip this check for auth pages and no-merchant whitelist pages
      if (
        initialState?.currentUser &&
        !authWhitelist.includes(pathname) &&
        !noMerchantWhitelist.some((p) => pathname.startsWith(p))
      ) {
        const userId =
          initialState.currentUser.id || initialState.currentUser.userid;
        const hasMerchants = checkUserHasMerchants(userId);

        // If user has no merchants, redirect to merchants page (shows empty state)
        if (!hasMerchants) {
          const role =
            initialState.currentUser.role || initialState.currentUser.access;
          if (role === 'MerchantOwner') {
            history.push(merchantsPath);
          } else {
            // Non-owners without merchants go to settings (they can't create merchants)
            history.push('/account/settings');
          }
        }
      }
    },
    menuHeaderRender: undefined,
    childrenRender: (children: React.ReactNode) => {
      const isSuperAdmin =
        initialState?.currentUser?.role === 'SuperAdmin' ||
        initialState?.currentUser?.access === 'SuperAdmin';
      const inMerchantMode = isSuperAdmin && !!getActingAsMerchantOwner();
      return (
        <>
          {inMerchantMode && <ActingAsMerchantBanner />}
          {(!isSuperAdmin || inMerchantMode) && <MerchantInitializer />}
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState: any) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request configuration
 * @doc https://umijs.org/docs/max/request
 */
export const request: RequestConfig = {
  baseURL: isDev
    ? ''
    : process.env.REACT_APP_API_URL || 'https://aimerpay-api.aimerhq.com',
  ...errorConfig,
};
